import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Screen, TopBar, Logo, BackButton, ScrollArea, BottomBar, PrimaryButton } from "@/components/jk/ui";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const email = location.state?.email;
  const [devCode, setDevCode] = useState(location.state?.devCode);
  const [emailFailed, setEmailFailed] = useState(location.state?.emailFailed);
  const [provider, setProvider] = useState(location.state?.provider);
  const isAuth0Otp = provider === "auth0";

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(location.state?.cooldown || 45);
  const inputs = useRef([]);

  useEffect(() => {
    if (!email) navigate("/signin", { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setInterval(() => setSeconds((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [seconds]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  const code = digits.join("");
  const complete = code.length === 6;

  const setDigit = (i, val) => {
    const v = val.replace(/\D/g, "");
    setError("");
    if (v.length > 1) {
      // paste
      const arr = v.slice(0, 6).split("");
      const next = ["", "", "", "", "", ""];
      arr.forEach((c, idx) => (next[idx] = c));
      setDigits(next);
      inputs.current[Math.min(arr.length, 5)]?.focus();
      return;
    }
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) inputs.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const verify = useCallback(async () => {
    if (!complete) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code });
      if (data.is_admin && data.admin_token) {
        // Admin signing in via the main flow → route to the admin dashboard.
        localStorage.setItem("jk_admin_token", data.admin_token);
        window.location.assign("/admin/overview");
        return;
      }
      login(data.token, data.astrologer, data.onboarding_complete);
      navigate(data.onboarding_complete ? "/dashboard" : "/onboarding/profile", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Verification failed.");
      setDigits(["", "", "", "", "", ""]);
      inputs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }, [complete, code, email, login, navigate]);

  useEffect(() => {
    if (complete && !loading) verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete]);

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/request-otp", { email });
      setSeconds(data.cooldown || 45);
      setDigits(["", "", "", "", "", ""]);
      inputs.current[0]?.focus();
      if (data.dev_code) setDevCode(data.dev_code);
      if (data.email_failed) setEmailFailed(true);
      if (data.provider) setProvider(data.provider);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not resend.");
    }
  };

  return (
    <Screen testid="otp-verify-screen">
      <TopBar left={<BackButton onClick={() => navigate(-1)} />} center={<Logo className="h-[16px] w-auto" />} />

      <ScrollArea className="px-6 pt-10">
        <h1 className="text-[32px] font-bold leading-[1.07] tracking-[-0.02em] text-[#111111]">Check your email</h1>
        <p className="mt-3 text-[14px] leading-relaxed text-[#666666] break-words">
          We sent a 6-digit code to <span className="font-semibold text-[#111111] break-all">{email}</span>
        </p>

        <div className="mt-8 grid grid-cols-6 gap-2" data-testid="otp-inputs">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              data-testid={`otp-digit-${i}`}
              inputMode="numeric"
              maxLength={6}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              className={`h-[54px] w-full min-w-0 rounded-[12px] border bg-white text-center text-[22px] font-bold text-[#111111] outline-none transition-colors ${
                complete ? "border-[#111111]" : d ? "border-[#111111]" : "border-[#e5e5e5]"
              }`}
            />
          ))}
        </div>

        {error && <p className="mt-3 text-[13px] text-[#d23b2f]" data-testid="otp-error">{error}</p>}

        {devCode && !isAuth0Otp && (
          <div className="mt-5 rounded-[12px] bg-[var(--jk-butter)] px-4 py-3 text-[12px] text-[#6b5d2a]" data-testid="dev-code-hint">
            {emailFailed ? (
              <>We couldn&apos;t deliver the email — your sign-in code is <span className="font-bold tracking-widest">{devCode}</span></>
            ) : (
              <>Dev mode — your code is <span className="font-bold tracking-widest">{devCode}</span> (email sending wires up with your Resend key)</>
            )}
          </div>
        )}

        <div className="mt-7 text-center">
          {seconds > 0 ? (
            <p className="text-[13px] text-[#aaaaaa]" data-testid="resend-countdown">
              Resend code in 0:{String(seconds).padStart(2, "0")}
            </p>
          ) : (
            <button data-testid="resend-button" onClick={resend} className="text-[13px] font-semibold text-[#111111] underline underline-offset-2">
              Resend code
            </button>
          )}
        </div>
      </ScrollArea>

      <BottomBar>
        <PrimaryButton testid="verify-button" onClick={verify} disabled={!complete || loading}>
          {loading ? "Verifying…" : "Verify"}
        </PrimaryButton>
      </BottomBar>
    </Screen>
  );
};

export default OtpVerification;
